import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, RefreshCw, Search, Filter, X, Eye, Loader2, UserCheck, UserX, Smartphone, MessageCircle, Send,
} from 'lucide-react';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { API } from '../utils/apiConfig';

const err = (e) => e?.response?.data?.detail || e?.message || 'Error';
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return v; }
};

const fmtDateOnly = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return v; }
};

export default function AppUsersPage() {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [profileFilter, setProfileFilter] = useState('all');
  const [sortBy, setSortBy] = useState('last_login_desc');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // WA Re-engagement state
  const [reengageOpen, setReengageOpen] = useState(false);
  const [reengagePreview, setReengagePreview] = useState(null);
  const [reengageForm, setReengageForm] = useState({
    profile_complete: 'all', cooldown_days: 7, message_text: '',
    use_template: false, template_name: '', limit: '',
  });
  const [reengageSending, setReengageSending] = useState(false);
  const [reengageResult, setReengageResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/app-users`, {
          params: { search, profile_complete: profileFilter, sort: sortBy, limit: 500 },
        }),
        axios.get(`${API}/app-users/summary`),
      ]);
      setUsers(r.data.users || []);
      setSummary(s.data || {});
    } catch (e) { toast.error(err(e)); }
    finally { setLoading(false); }
  }, [search, profileFilter, sortBy]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (u) => {
    setDetail({ user: u, bookings: [] });
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/app-users/${u.id}`);
      setDetail(r.data);
    } catch (e) { toast.error(err(e)); }
    finally { setDetailLoading(false); }
  };

  const clearFilters = () => {
    setSearch(''); setProfileFilter('all'); setSortBy('last_login_desc');
  };

  const openReengage = () => {
    setReengageOpen(true);
    setReengagePreview(null);
    setReengageResult(null);
    setReengageForm(f => ({ ...f, profile_complete: profileFilter }));
  };

  const previewReengage = async () => {
    try {
      const res = await axios.post(`${API}/app-users/reengage`, {
        ...reengageForm, dry_run: true, limit: reengageForm.limit || null,
      });
      setReengagePreview(res.data);
    } catch (e) { toast.error(err(e)); }
  };

  const handleSendReengage = async () => {
    setReengageSending(true);
    try {
      const res = await axios.post(`${API}/app-users/reengage`, {
        ...reengageForm, dry_run: false, limit: reengageForm.limit || null,
      });
      setReengageResult(res.data);
      if (res.data.sent > 0) toast.success(`Sent to ${res.data.sent} user(s)`);
      else toast.warning('No messages sent — check WhatsApp setup');
    } catch (e) {
      toast.error(err(e));
    } finally {
      setReengageSending(false);
    }
  };

  const rows = useMemo(() => users, [users]);

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6" data-testid="app-users-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Smartphone className="w-6 h-6 sm:w-7 sm:h-7 text-primary" /> APP Users
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Every player who has registered or logged in via the MAKSIO customer app.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={openReengage}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="app-users-reengage-btn"
            >
              <MessageCircle className="w-4 h-4 mr-1" /> Re-engage
            </Button>
            <Button size="sm" variant="outline" onClick={load} data-testid="refresh-app-users-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard icon={<Users className="w-4 h-4" />} label="Total Users" value={summary.total || 0} accent="text-slate-900" />
          <SummaryCard icon={<UserCheck className="w-4 h-4" />} label="Profile Complete" value={summary.profile_complete || 0} accent="text-emerald-600" />
          <SummaryCard icon={<UserX className="w-4 h-4" />} label="Profile Incomplete" value={summary.profile_incomplete || 0} accent="text-amber-600" />
          <SummaryCard icon={<Smartphone className="w-4 h-4" />} label="Ever Logged In" value={summary.logged_in || 0} accent="text-indigo-600" />
        </div>

        {/* Filters */}
        <Card className="p-3 flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs text-slate-500">Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="name / mobile / email / DUPR" className="w-[240px] pl-8"
                     data-testid="app-users-search" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Profile</Label>
            <Select value={profileFilter} onValueChange={setProfileFilter}>
              <SelectTrigger className="w-[160px]" data-testid="app-users-profile-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Profiles</SelectItem>
                <SelectItem value="complete">Complete Only</SelectItem>
                <SelectItem value="incomplete">Incomplete Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Sort By</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="app-users-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="last_login_desc">Latest Login</SelectItem>
                <SelectItem value="created_desc">Newest Signup</SelectItem>
                <SelectItem value="created_asc">Oldest Signup</SelectItem>
                <SelectItem value="name_asc">Name (A→Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="app-users-clear-filters">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
            <Button size="sm" onClick={load} data-testid="app-users-apply-filters">
              <Filter className="w-4 h-4 mr-1" /> Apply
            </Button>
          </div>
        </Card>

        {/* Table */}
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Mobile</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Profile</th>
                <th className="px-3 py-3">Preferred Game</th>
                <th className="px-3 py-3">Skill</th>
                <th className="px-3 py-3">Last Booking</th>
                <th className="px-3 py-3">Signed Up</th>
                <th className="px-3 py-3">Last Login</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="app-users-tbody">
              {loading ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">
                  No app users found.</td></tr>
              ) : rows.map((u) => (
                <tr key={u.id} className="border-b hover:bg-slate-50/60" data-testid={`app-user-row-${u.id}`}>
                  <td className="px-3 py-3 font-medium text-slate-900">
                    {u.name || <span className="text-slate-400 italic">—</span>}
                    {u.dupr_id && <div className="text-[10px] text-slate-500">DUPR: {u.dupr_id}</div>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap font-mono">{u.mobile}</td>
                  <td className="px-3 py-3 text-slate-600">{u.email || '—'}</td>
                  <td className="px-3 py-3">
                    {u.is_profile_complete
                      ? <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-700 font-medium">Complete</span>
                      : <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-700 font-medium">Incomplete</span>}
                  </td>
                  <td className="px-3 py-3 text-slate-700">{u.most_played_game || '—'}</td>
                  <td className="px-3 py-3 text-slate-700">{u.skill_level || '—'}</td>
                  <td className="px-3 py-3 text-xs">
                    {u.last_booking ? (
                      <>
                        <div className="font-medium text-slate-800">{u.last_booking.booking_id}</div>
                        <div className="text-slate-500">
                          {fmtDateOnly(u.last_booking.date)} · {u.last_booking.start_time}
                        </div>
                        <div className="text-slate-500 truncate max-w-[180px]">{u.last_booking.venue}</div>
                      </>
                    ) : <span className="text-slate-400">Never booked</span>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs text-slate-600">
                    {u.last_login_at ? fmtDate(u.last_login_at) : <span className="text-slate-400">—</span>}
                    {u.login_count > 0 && (
                      <div className="text-[10px] text-slate-400">{u.login_count} login{u.login_count > 1 ? 's' : ''}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(u)}
                            data-testid={`view-app-user-${u.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Detail dialog */}
        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="app-user-detail-dialog">
            <DialogHeader>
              <DialogTitle>
                {detail?.user?.name || 'App User'} <span className="text-sm font-mono text-slate-500">· {detail?.user?.mobile}</span>
              </DialogTitle>
            </DialogHeader>
            {detail && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <ProfileField label="Email" value={detail.user.email} />
                  <ProfileField label="Gender" value={detail.user.gender} />
                  <ProfileField label="Date of Birth" value={detail.user.date_of_birth} />
                  <ProfileField label="Skill Level" value={detail.user.skill_level} />
                  <ProfileField label="Preferred Game" value={detail.user.most_played_game} />
                  <ProfileField label="Preferred Venue" value={detail.user.preferred_venue} />
                  <ProfileField label="DUPR ID" value={detail.user.dupr_id} />
                  <ProfileField label="Short Address" value={detail.user.short_address} />
                  <ProfileField label="Signed Up" value={fmtDate(detail.user.created_at)} />
                  <ProfileField label="Last Login" value={detail.user.last_login_at ? fmtDate(detail.user.last_login_at) : 'Never'} />
                  <ProfileField label="Login Count" value={detail.user.login_count || 0} />
                  <ProfileField label="Profile Reward Claimed" value={detail.user.profile_reward_claimed ? 'Yes' : 'No'} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800 mb-1">Recent Bookings</div>
                  {detailLoading ? (
                    <div className="text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</div>
                  ) : (detail.bookings || []).length === 0 ? (
                    <div className="text-slate-400 text-sm">No bookings for this user.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b">
                          <tr className="text-left text-slate-600">
                            <th className="px-2 py-2">Booking ID</th>
                            <th className="px-2 py-2">Date</th>
                            <th className="px-2 py-2">Time</th>
                            <th className="px-2 py-2">Venue / Court</th>
                            <th className="px-2 py-2">Game</th>
                            <th className="px-2 py-2 text-right">Amount</th>
                            <th className="px-2 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.bookings || []).map((b) => (
                            <tr key={b.booking_id} className="border-b">
                              <td className="px-2 py-2 font-mono">{b.booking_id}</td>
                              <td className="px-2 py-2 whitespace-nowrap">{fmtDateOnly(b.date)}</td>
                              <td className="px-2 py-2 whitespace-nowrap">{b.start_time} - {b.end_time}</td>
                              <td className="px-2 py-2">
                                <div>{b.venue}</div>
                                <div className="text-slate-500">{b.court}</div>
                              </td>
                              <td className="px-2 py-2">{b.game}</td>
                              <td className="px-2 py-2 text-right">{inr(b.amount)}</td>
                              <td className="px-2 py-2">
                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-700">
                                  {b.status || '—'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* WA Re-engagement Dialog */}
        <Dialog open={reengageOpen} onOpenChange={(o) => { setReengageOpen(o); if (!o) { setReengagePreview(null); setReengageResult(null); } }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-emerald-600" />
                WA Re-engagement — App Users
              </DialogTitle>
            </DialogHeader>

            {reengageResult ? (
              <div className="space-y-3 py-2" data-testid="app-users-reengage-result">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-slate-800">{reengageResult.matched}</p>
                    <p className="text-xs text-slate-500">Matched</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-emerald-700">{reengageResult.sent}</p>
                    <p className="text-xs text-slate-500">Sent</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-red-700">{reengageResult.failed}</p>
                    <p className="text-xs text-slate-500">Failed</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center">{reengageResult.skipped_cooldown} skipped (cooldown)</p>
              </div>
            ) : (
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs text-slate-600">Profile Status</Label>
                  <Select value={reengageForm.profile_complete} onValueChange={(v) => setReengageForm(f => ({ ...f, profile_complete: v }))}>
                    <SelectTrigger data-testid="app-users-reengage-profile"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All App Users</SelectItem>
                      <SelectItem value="complete">Profile Complete</SelectItem>
                      <SelectItem value="incomplete">Profile Incomplete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Cooldown Days</Label>
                  <Input type="number" value={reengageForm.cooldown_days} min={0}
                    onChange={(e) => setReengageForm(f => ({ ...f, cooldown_days: parseInt(e.target.value) || 0 }))}
                    data-testid="app-users-reengage-cooldown" />
                  <p className="text-xs text-slate-400 mt-1">Skip users already messaged within this many days</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Message Text</Label>
                  <textarea
                    value={reengageForm.message_text}
                    onChange={(e) => setReengageForm(f => ({ ...f, message_text: e.target.value }))}
                    placeholder="Hi {name}, we miss you at MAKS! Book your next session now."
                    rows={3}
                    data-testid="app-users-reengage-message"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  />
                  <p className="text-xs text-slate-400 mt-1">Use {'{name}'} for personalisation</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Max Recipients (optional)</Label>
                  <Input type="number" value={reengageForm.limit} min={1}
                    onChange={(e) => setReengageForm(f => ({ ...f, limit: e.target.value }))}
                    placeholder="Leave blank for all eligible"
                    data-testid="app-users-reengage-limit" />
                </div>
                {reengagePreview && (
                  <div className="bg-emerald-50 rounded-lg p-3 text-sm border border-emerald-200" data-testid="app-users-reengage-preview">
                    <p><strong>{reengagePreview.matched}</strong> matched · <strong>{reengagePreview.eligible}</strong> eligible after cooldown · <strong>{reengagePreview.skipped_cooldown}</strong> skipped</p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setReengageOpen(false)} disabled={reengageSending}>
                {reengageResult ? 'Close' : 'Cancel'}
              </Button>
              {!reengageResult && (
                <>
                  <Button variant="outline" onClick={previewReengage} data-testid="app-users-reengage-preview-btn">
                    Preview
                  </Button>
                  <Button
                    onClick={handleSendReengage}
                    disabled={reengageSending || (reengagePreview && reengagePreview.eligible === 0)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="app-users-reengage-send-btn"
                  >
                    {reengageSending
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                      : <><Send className="w-4 h-4 mr-2" />Send{reengagePreview ? ` to ${reengagePreview.eligible}` : ''}</>
                    }
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}


function SummaryCard({ icon, label, value, accent }) {
  return (
    <Card className="p-3 sm:p-4">
      <div className="flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">
        {icon} {label}
      </div>
      <div className={`text-lg sm:text-2xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</div>
    </Card>
  );
}


function ProfileField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-slate-800">{value === undefined || value === '' || value === null ? <span className="text-slate-400 italic">—</span> : value}</div>
    </div>
  );
}
